import { AVAILABLE_CURRENCIES } from './formatters';

const API_KEY = '06c535aef1ed32e3d18e6ef2';
const BASE_URL = 'https://v6.exchangerate-api.com/v6';

interface ExchangeRates {
  [key: string]: number;
}

let cachedRates: { [key: string]: ExchangeRates } = {};
let lastFetchTime: number = 0;
const CACHE_DURATION = 1000 * 60 * 60; // 1 hour

export async function verifyApiConnection(): Promise<boolean> {
  try {
    const response = await fetch(`${BASE_URL}/${API_KEY}/latest/USD`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.result === 'success';
  } catch (error) {
    console.error('API Connection Error:', error);
    return false;
  }
}

export async function getExchangeRates(baseCurrency: string = 'USD'): Promise<ExchangeRates> {
  try {
    // Check cache first
    const now = Date.now();
    if (cachedRates[baseCurrency] && (now - lastFetchTime < CACHE_DURATION)) {
      return cachedRates[baseCurrency];
    }

    const response = await fetch(`${BASE_URL}/${API_KEY}/latest/${baseCurrency}`);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.result === 'error') {
      throw new Error(data['error-type']);
    }

    cachedRates[baseCurrency] = data.conversion_rates;
    lastFetchTime = now;
    
    return data.conversion_rates;
  } catch (error) {
    console.error('Error fetching exchange rates:', error);
    
    // Fallback to default rates if API fails
    return getFallbackRates(baseCurrency);
  }
}

function getFallbackRates(baseCurrency: string): ExchangeRates {
  // Default exchange rates as fallback
  const defaultRates: ExchangeRates = {
    USD: 1.0,
    EUR: 0.85,
    GBP: 0.73,
    JPY: 110.0,
    CNY: 6.45,
    INR: 74.5,
  };

  if (baseCurrency === 'USD') {
    return defaultRates;
  }

  // Convert rates to requested base currency
  const baseRate = defaultRates[baseCurrency] || 1;
  const convertedRates: ExchangeRates = {};

  Object.entries(defaultRates).forEach(([currency, rate]) => {
    convertedRates[currency] = rate / baseRate;
  });

  return convertedRates;
}

export async function convertAmount(
  amount: number,
  fromCurrency: string,
  toCurrency: string
): Promise<number> {
  try {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    const rates = await getExchangeRates(fromCurrency);
    const rate = rates[toCurrency];

    if (!rate) {
      throw new Error(`Exchange rate not found for ${toCurrency}`);
    }

    return amount * rate;
  } catch (error) {
    console.error('Error converting currencies:', error);
    return amount; // Return original amount if conversion fails
  }
}

export async function convertAllAmounts(
  amounts: { [key: string]: number },
  fromCurrency: string,
  toCurrency: string
): Promise<{ [key: string]: number }> {
  try {
    if (fromCurrency === toCurrency) {
      return amounts;
    }

    const rates = await getExchangeRates(fromCurrency);
    const rate = rates[toCurrency];

    if (!rate) {
      throw new Error(`Exchange rate not found for ${toCurrency}`);
    }

    const convertedAmounts: { [key: string]: number } = {};
    Object.entries(amounts).forEach(([key, value]) => {
      convertedAmounts[key] = value * rate;
    });

    return convertedAmounts;
  } catch (error) {
    console.error('Error converting currencies:', error);
    return amounts; // Return original amounts if conversion fails
  }
}

export type { ExchangeRates };